import { useEffect, useMemo, useRef, useState } from 'react'
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  ReferenceDot,
  ResponsiveContainer,
} from 'recharts'
import axios from 'axios'
import { Spinner } from '@/components/Spinner'
import { useBatteryReport } from '@/hooks/useBatteryReport'
import {
  runBatterySimulation,
  dailyPriceSpreads,
  DEFAULT_TRADING_SHARE_PERCENT,
  ROUND_TRIP_EFFICIENCY,
  TRADING_REALISM_FACTOR,
  type BatteryStrategy,
} from '@/lib/batterySimulation'
import {
  sweepCapacities,
  recommendCapacity,
  recommendFullAdvice,
  STANDARD_BATTERY_CAPACITIES_KWH,
  defaultPackEurPerKwh,
  STANDBY_KWH_PER_YEAR,
  ANNUAL_DEGRADATION_RATE,
  WARRANTY_YEARS,
  EXPECTED_BATTERY_LIFETIME_YEARS,
  MAX_BATTERY_LIFETIME_YEARS,
  cumulativeSavingsWithDegradation,
  hasFinancialCase,
  priceModeForContract,
  type BatteryType,
  type CapacitySweepRow,
  type EnergyContract,
} from '@/lib/batterySizing'
import { prepareAnnualSlots, solarEstimateIsUncertain, NED_PROFILE_YEAR } from '@/lib/nedProfiles'
import { rangedRow, SHOWN_RANGE_LEVEL, type RangedRow, type SavingsRange } from '@/lib/batteryRanges'
import {
  DEFAULT_FEED_IN_FEE_EUR_PER_KWH,
  SUPPLIER_MARKUP_EUR_PER_KWH,
  fixedTariff,
  spotTariff,
  type HourlyTariff,
} from '@/lib/tariffs'
import {
  probeAddressSituation,
  type AddressSituation,
  type HourlySlot,
} from '@/lib/energyHistory'
import { APP_STORE_URL, PLAY_STORE_URL } from '@/lib/branding'
import { trackEvent } from '@/lib/analytics'
import { EasyEnergyLogo } from '@/components/AppLayout'
import type { QuietDayReason, QuietDaysSummary } from '@/lib/batteryReportPdf'
import { AdviceStepper } from './AdviceStepper'
import { QuickWizard, type QuickWizardResult } from './QuickWizard'
import { ExpertForm } from './ExpertForm'
import { dataQuality } from './DataQualityCard'

type PriceMode = 'fixed' | 'spot'
type View = 'questions' | 'expert' | 'report' | 'no-case'

/** Preset usage combinations users can toggle on the report (always dynamic). */
type UsageScenarioId = 'self-dynamic' | 'trading' | 'hybrid-50'

interface UsageScenario {
  id: UsageScenarioId
  title: string
  description: string
  strategy: BatteryStrategy
  priceMode: PriceMode
  tradingShare: number
}

const USAGE_SCENARIOS: UsageScenario[] = [
  {
    id: 'self-dynamic',
    title: 'Zelfverbruik',
    description: 'Maximaal eigen zonnestroom gebruiken met uurprijzen.',
    strategy: 'self-consumption',
    priceMode: 'spot',
    tradingShare: DEFAULT_TRADING_SHARE_PERCENT,
  },
  {
    id: 'trading',
    title: 'Alleen handelen',
    description: 'Batterij alleen voor arbitrage op dynamische uurprijzen.',
    strategy: 'trading',
    priceMode: 'spot',
    tradingShare: DEFAULT_TRADING_SHARE_PERCENT,
  },
  {
    id: 'hybrid-50',
    title: 'Automatisch · zon eerst, dan handelen',
    description:
      'Zonnestroom gaat voor; wat de zon niet vult, handelt op uurprijzen — vooral in de winter.',
    strategy: 'self-consumption-trading',
    priceMode: 'spot',
    tradingShare: DEFAULT_TRADING_SHARE_PERCENT,
  },
]

function matchUsageScenario(strategy: BatteryStrategy): UsageScenarioId | null {
  if (strategy === 'self-consumption') return 'self-dynamic'
  if (strategy === 'trading') return 'trading'
  // Any hybrid share maps to this card; selecting it resets to the default share.
  if (strategy === 'self-consumption-trading') return 'hybrid-50'
  return null
}

// Simulation defaults (mirroring the companion-web battery report).
// Min/max SoC are adjustable in the expert panel; these are the defaults.
const DEFAULT_MIN_SOC_PERCENT = 10
const DEFAULT_MAX_SOC_PERCENT = 100
// Fallback size when no capacity clearly wins (e.g. zero savings all round).
const DEFAULT_CAPACITY_KWH = 10

// easyEnergy brand colors for charts (green savings, navy investment, orange break-even)
const CHART = {
  grid: '#DDDDDD',
  tick: '#858F9C',
  savings: '#77AF00',
  investment: '#181E34',
  breakEven: '#FF6600',
}

const eur = (v: number) => `€ ${v.toLocaleString('nl-NL', { maximumFractionDigits: 0 })}`
const yearsFmt = (v: number) =>
  Number.isFinite(v) ? `${v.toFixed(1).replace('.', ',')} jaar` : '—'
const kwhLabel = (v: number) =>
  `${v.toLocaleString('nl-NL', { maximumFractionDigits: 1 })} kWh`
const eurRange = (range: SavingsRange) => `${eur(range.lowEur)} – ${eur(range.highEur)}`
const RANGE_EXPLANATION =
  'Bij elke batterij staat ook een bandbreedte: bij 8 van de 10 huishoudens waarvoor we dit met een heel jaar meetdata hebben nagerekend, viel de werkelijke besparing daarbinnen.'
/** Said when the shown savings moved to the middle of the range (rangedRow). */
const RANGE_ADJUSTED = {
  up: 'Met een meetperiode als de jouwe kwam onze berekening bij bijna al die huishoudens te laag uit, dus tonen we als besparing het midden van de bandbreedte.',
  down: 'Met een meetperiode als de jouwe kwam onze berekening bij bijna al die huishoudens te hoog uit, dus tonen we als besparing het midden van de bandbreedte.',
} as const
/** "6,6 – 9,1 jaar", or open-ended when the low end of the savings never pays back. */
const yearsRange = (range: SavingsRange) => {
  const years = (v: number) => v.toFixed(1).replace('.', ',')
  if (!Number.isFinite(range.paybackLowYears)) return null
  return Number.isFinite(range.paybackHighYears)
    ? `${years(range.paybackLowYears)} – ${years(range.paybackHighYears)} jaar`
    : `${years(range.paybackLowYears)} jaar of langer`
}

/**
 * Estimated savings per calendar month for one simulated battery (PDF appendix).
 * Each kWh is valued on the side of the meter it crosses, the same rule the
 * simulation uses. Exact for self-consumption and for pure trading; for the
 * hybrid it splits discharge over what the house still needs, which the
 * compartments only settle at the end, so its shape is indicative.
 */
function monthlySavings(
  slots: HourlySlot[],
  hourly: { time: string; chargeWh: number; dischargeWh: number }[],
  strategy: BatteryStrategy,
  tariff: HourlyTariff
): Array<{ month: string; savings: number }> {
  const byMonth = new Map<string, number>()
  for (let i = 0; i < slots.length; i++) {
    const hr = hourly[i]
    const slot = slots[i]
    if (!hr || !slot) continue
    const importPrice = tariff.importEurPerKwh(hr.time)
    const exportPrice = tariff.exportEurPerKwh(hr.time)
    let value: number
    if (strategy === 'self-consumption') {
      value = (hr.dischargeWh / 1000) * importPrice - (hr.chargeWh / 1000) * exportPrice
    } else if (strategy === 'trading') {
      value = (hr.dischargeWh / 1000) * exportPrice - (hr.chargeWh / 1000) * importPrice
    } else {
      const needWh = Math.max(0, slot.delivered - slot.returned)
      const surplusWh = Math.max(0, slot.returned - slot.delivered)
      const toHomeWh = Math.min(hr.dischargeWh, needWh)
      const fromSurplusWh = Math.min(hr.chargeWh, surplusWh)
      value =
        (toHomeWh / 1000) * importPrice +
        ((hr.dischargeWh - toHomeWh) / 1000) * exportPrice -
        (fromSurplusWh / 1000) * exportPrice -
        ((hr.chargeWh - fromSurplusWh) / 1000) * importPrice
    }
    byMonth.set(hr.time.slice(0, 7), (byMonth.get(hr.time.slice(0, 7)) ?? 0) + value)
  }
  return [...byMonth.entries()]
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([month, savings]) => ({ month, savings }))
}

type CapacityVariant = 'compact' | 'balance' | 'roomy'

/** A size may only be recommended when it earns back its own cost in its lifetime. */
function isWorthRecommending(row: CapacitySweepRow | undefined): boolean {
  return (
    row != null &&
    row.annualSavingsEur > 0 &&
    Number.isFinite(row.paybackYears) &&
    row.paybackYears <= MAX_BATTERY_LIFETIME_YEARS
  )
}

/**
 * Label the recommended size "Ons advies" and its adjacent standard sizes
 * "Instap" / "Voor groei". Only sizes that pay themselves back get a label, so
 * a battery that never breaks even is never dressed up as a growth option, and
 * when the recommendation itself does not pay back nothing is labelled at all.
 */
function labelCapacities(
  rows: CapacitySweepRow[],
  recommendedKwh: number
): Map<number, CapacityVariant> {
  const labels = new Map<number, CapacityVariant>()
  const rowFor = (kwh: number) => rows.find((r) => r.capacityKwh === kwh)
  if (!isWorthRecommending(rowFor(recommendedKwh))) return labels

  labels.set(recommendedKwh, 'balance')
  const standards: number[] = [...STANDARD_BATTERY_CAPACITIES_KWH]
  const idx = standards.indexOf(recommendedKwh)
  const smaller = idx > 0 ? standards[idx - 1] : null
  const larger = idx >= 0 && idx < standards.length - 1 ? standards[idx + 1] : null
  if (smaller != null && isWorthRecommending(rowFor(smaller))) labels.set(smaller, 'compact')
  if (larger != null && isWorthRecommending(rowFor(larger))) labels.set(larger, 'roomy')
  return labels
}

const VARIANT_META: Record<
  CapacityVariant,
  {
    title: string
    tag: string
    tagClass: string
    barColor: string
    description: (kwh: number) => string
  }
> = {
  compact: {
    title: 'Compact',
    tag: 'Instap',
    tagClass: 'bg-beige text-dark-blue',
    barColor: '#D5D3CE',
    description: (kwh) =>
      `Een compacte ${kwhLabel(kwh)}-batterij — goedkoper in aanschaf, iets minder besparing.`,
  },
  balance: {
    title: 'Balans',
    tag: 'Ons advies',
    tagClass: 'bg-dark-purple text-white',
    barColor: '#6245DE',
    description: (kwh) =>
      `Onze aanbeveling: ${kwhLabel(kwh)} combineert een snelle terugverdientijd met zoveel mogelijk eigen verbruik.`,
  },
  roomy: {
    title: 'Ruim',
    tag: 'Voor groei',
    tagClass: 'bg-[#FFE8CC] text-orange',
    barColor: '#FF8500',
    description: (kwh) =>
      `Extra ruimte (${kwhLabel(kwh)}) voor meer eigen verbruik of toekomstige apparaten.`,
  },
}

const OTHER_CARD = {
  tag: null as string | null,
  tagClass: '',
  barColor: '#D5D3CE',
  description: (kwh: number) =>
    `Een thuisbatterij van ${kwhLabel(kwh)} — bekijk wat die jou oplevert.`,
}

/**
 * Honest outcome when a battery cannot pay off: either no earning mechanism
 * exists (fixed tariffs without solar) or the simulation finds no size that
 * recovers its own cost.
 */
function NoFinancialCaseCard({
  variant,
  fixedConsumption,
  onBack,
  onOpenExpert,
}: {
  variant: 'no-mechanism' | 'no-savings'
  fixedConsumption: number
  onBack: () => void
  onOpenExpert: () => void
}) {
  return (
    <div className="card p-6 md:p-[30px]">
      <h2 className="max-w-2xl text-28 font-semibold leading-111 lg:text-44">
        Een thuisbatterij levert jou nu niets op
      </h2>
      {variant === 'no-mechanism' ? (
        <>
          <p className="mt-4 max-w-2xl text-base leading-normal text-dark-blue/70">
            Een thuisbatterij verdient op twee manieren geld: je eigen zonnestroom bewaren in
            plaats van terugleveren, of laden als stroom goedkoop is en gebruiken als stroom duur
            is. Zonder zonnepanelen valt de eerste manier weg. En met een vast tarief van{' '}
            € {fixedConsumption.toFixed(2).replace('.', ',')} per kWh betaal je elk uur van de dag
            precies hetzelfde, dus is er ook geen prijsverschil om te benutten.
          </p>
          <p className="mt-3 max-w-2xl text-base leading-normal text-dark-blue/70">
            We rekenen dit daarom niet door: elke batterijgrootte zou je hier geld kosten in plaats
            van opleveren. Dat verandert zodra je zonnepanelen hebt, of als je overstapt op een
            dynamisch contract met uurprijzen.
          </p>
        </>
      ) : (
        <p className="mt-4 max-w-2xl text-base leading-normal text-dark-blue/70">
          We hebben alle batterijgroottes op jouw eigen energiedata doorgerekend, maar geen enkele
          levert genoeg op om de aanschaf terug te verdienen. Dat komt meestal doordat er te weinig
          zonnestroom overblijft om te bewaren, of doordat het prijsverschil tussen de goedkope en
          dure uren te klein is.
        </p>
      )}
      <div className="mt-6 flex flex-wrap gap-3">
        <button className="btn-primary" onClick={onBack}>
          Pas je antwoorden aan
        </button>
        <button className="btn-secondary" onClick={onOpenExpert}>
          Open de expertinstellingen
        </button>
      </div>
    </div>
  )
}

export function BatteryReport({
  addressUuid,
  addressLabel,
}: {
  addressUuid: string
  addressLabel: string
}) {
  const [months, setMonths] = useState(12)
  // easyEnergy customers are always on a dynamic contract.
  const [contract, setContract] = useState<EnergyContract>('dynamic')
  const [strategy, setStrategy] = useState<BatteryStrategy>('self-consumption')
  const [tradingShare, setTradingShare] = useState(DEFAULT_TRADING_SHARE_PERCENT)
  const [priceMode, setPriceMode] = useState<PriceMode>('spot')
  const fixedConsumption = 0.26
  const fixedReturn = 0.04
  const [feedInFee, setFeedInFee] = useState(DEFAULT_FEED_IN_FEE_EUR_PER_KWH)
  const [supplierMarkup, setSupplierMarkup] = useState(SUPPLIER_MARKUP_EUR_PER_KWH)
  const [batteryType, setBatteryType] = useState<BatteryType>('installed')
  const [batteryCostPerKwh, setBatteryCostPerKwh] = useState(() =>
    defaultPackEurPerKwh('installed')
  )
  const [minSoc, setMinSoc] = useState(DEFAULT_MIN_SOC_PERCENT)
  const [maxSoc, setMaxSoc] = useState(DEFAULT_MAX_SOC_PERCENT)
  const [inverterSel, setInverterSel] = useState<number | null>(null)
  const [pdfBusy, setPdfBusy] = useState(false)
  const [showMethodDetails, setShowMethodDetails] = useState(false)
  // User pick among the carousel cards; null = follow the recommendation.
  const [capacitySel, setCapacitySel] = useState<number | null>(null)
  const carouselRef = useRef<HTMLDivElement>(null)
  const didCenterCarousel = useRef(false)
  const scenarioTouchedRef = useRef(false)
  const batteryTypeTouchedRef = useRef(false)
  const didAutoAdviseRef = useRef(false)
  const analysisEntryRef = useRef<'wizard' | 'expert'>('wizard')
  const reportReadyKeyRef = useRef<string | null>(null)
  const failTrackedKeyRef = useRef<string | null>(null)

  const setBatteryTypeAndPack = (type: BatteryType, fromUser = false) => {
    if (fromUser) {
      batteryTypeTouchedRef.current = true
      trackEvent('battery_type_select', {
        battery_type: type,
        previous_battery_type: batteryType,
      })
    }
    setBatteryType(type)
    setBatteryCostPerKwh(defaultPackEurPerKwh(type))
  }

  const applyUsageScenario = (id: UsageScenarioId, fromUser = false) => {
    const scenario = USAGE_SCENARIOS.find((s) => s.id === id)
    if (!scenario) return
    if (fromUser) {
      scenarioTouchedRef.current = true
      trackEvent('battery_scenario_select', {
        scenario_id: id,
        strategy: scenario.strategy,
        price_mode: scenario.priceMode,
        previous_scenario_id: matchUsageScenario(strategy) ?? undefined,
      })
    }
    setStrategy(scenario.strategy)
    setPriceMode(scenario.priceMode)
    setTradingShare(scenario.tradingShare)
    setCapacitySel(null)
  }

  const activeScenarioId = matchUsageScenario(strategy)

  const [situation, setSituation] = useState<AddressSituation | null>(null)
  const [situationLoading, setSituationLoading] = useState(true)
  const monthsTouchedRef = useRef(false)
  useEffect(() => {
    const controller = new AbortController()
    setSituationLoading(true)
    probeAddressSituation(addressUuid, controller.signal)
      .then((s) => {
        setSituation(s)
        if (s.months > 0 && !monthsTouchedRef.current) setMonths(s.months)
      })
      .catch(() => setSituation(null))
      .finally(() => {
        if (!controller.signal.aborted) setSituationLoading(false)
      })
    return () => controller.abort()
  }, [addressUuid])

  const { status, progress, data, generatedAt, error, run, cancel } = useBatteryReport(
    addressUuid,
    months
  )

  const [view, setView] = useState<View>(() => (status === 'done' ? 'report' : 'questions'))

  const running = status === 'running'
  const progressPct =
    progress.total > 0 ? Math.min(100, Math.round((progress.done / progress.total) * 100)) : 0

  const effectivePriceMode: PriceMode = strategy === 'self-consumption' ? priceMode : 'spot'
  const priceMap = useMemo(() => new Map(data?.priceEntries ?? []), [data])
  const peakInverterKw = data?.inverterKw ?? 0
  const hasSolarStrategy = strategy !== 'trading'

  const annualSeries = useMemo(() => {
    if (!data || data.slots.length === 0) return null
    return prepareAnnualSlots({
      measuredSlots: data.slots,
      measuredPeriodDays: data.periodDays,
      priceEntries: data.priceEntries,
    })
  }, [data])

  const economicsSlots = annualSeries?.slots ?? null
  const economicsPeriodDays = annualSeries?.periodDays ?? data?.periodDays ?? 0

  /** Measured over a stretch holding almost none of this household's solar year. */
  const solarEstimateUncertain = annualSeries != null && solarEstimateIsUncertain(annualSeries)
  const economicsPriceMap = useMemo(() => {
    if (!annualSeries) return priceMap
    if (annualSeries.usedProfileCompletion && annualSeries.priceEntries) {
      return new Map(annualSeries.priceEntries)
    }
    return priceMap
  }, [annualSeries, priceMap])

  /**
   * One tariff for everything on screen: the sweep, the selected simulation and
   * the monthly chart. A kWh has to be worth the same in all three.
   */
  const tariff = useMemo(
    () =>
      effectivePriceMode === 'spot'
        ? spotTariff(economicsPriceMap, {
            feedInFeeEurPerKwh: feedInFee,
            supplierMarkupEurPerKwh: supplierMarkup,
          })
        : fixedTariff(fixedConsumption, fixedReturn, { feedInFeeEurPerKwh: feedInFee }),
    [
      effectivePriceMode,
      economicsPriceMap,
      fixedConsumption,
      fixedReturn,
      feedInFee,
      supplierMarkup,
    ]
  )

  const rawSweep = useMemo(() => {
    if (!economicsSlots || economicsSlots.length === 0) return null
    return sweepCapacities({
      hourlySlots: economicsSlots,
      periodDays: economicsPeriodDays,
      peakInverterKw,
      inverterOverrideKw: inverterSel,
      batteryType,
      strategy,
      tariff,
      priceMode: effectivePriceMode,
      batteryCostPerKwh,
      minSocPercent: minSoc,
      maxSocPercent: maxSoc,
      // Start at the reserve floor: charge the battery arrives with would
      // otherwise be discharged as savings without ever being paid for, and the
      // annualisation below would then count that one-off several times over.
      initialSocPercent: minSoc,
      tradingSharePercent: tradingShare,
    })
  }, [
    economicsSlots,
    economicsPeriodDays,
    peakInverterKw,
    inverterSel,
    batteryType,
    strategy,
    effectivePriceMode,
    tariff,
    batteryCostPerKwh,
    tradingShare,
    minSoc,
    maxSoc,
  ])

  /**
   * The window a range is read for: only a filled-in year has one. A measured full
   * year needs none.
   */
  const rangeWindow = useMemo(
    () =>
      data && annualSeries?.usedProfileCompletion
        ? {
            weightA: annualSeries.weightA,
            weightI: annualSeries.weightI,
            reportDate: new Date(data.toIso),
            level: SHOWN_RANGE_LEVEL,
          }
        : null,
    [data, annualSeries]
  )

  /**
   * Every size with the range around its estimate: where the real year can land,
   * the range 8 in 10 households fell in when their own full year was checked. Where
   * the estimate falls outside its own range, the shown savings move to the middle
   * of it (rangedRow). A scenario the table was never calibrated on gets none.
   */
  const ranged = useMemo<RangedRow[] | null>(() => {
    if (!rawSweep) return null
    return rawSweep.rows.map((row) =>
      rangeWindow && activeScenarioId
        ? rangedRow(row, { ...rangeWindow, batteryType, scenarioId: activeScenarioId })
        : { row, range: null, adjusted: null }
    )
  }, [rawSweep, rangeWindow, batteryType, activeScenarioId])
  const sweep = useMemo(() => (ranged ? { rows: ranged.map((r) => r.row) } : null), [ranged])

  /**
   * Scenarios the user's contract can actually be advised on. Without this the
   * auto-advice below would happily move a fixed-tariff household onto hourly
   * prices, because trading always earns more on paper.
   */
  const contractScenarios = useMemo(
    () => USAGE_SCENARIOS.filter((s) => s.priceMode === priceModeForContract(contract)),
    [contract]
  )

  /** Best battery type + scenario across the allowed presets (independent of current toggles). */
  const autoAdvice = useMemo(() => {
    if (!economicsSlots || economicsSlots.length === 0) return null
    return recommendFullAdvice({
      scenarios: contractScenarios.map((s) => ({
        id: s.id,
        isTradeOnly: s.id === 'trading',
        strategy: s.strategy,
        priceMode: s.priceMode,
        tradingSharePercent: s.tradingShare,
      })),
      base: {
        hourlySlots: economicsSlots,
        periodDays: economicsPeriodDays,
        peakInverterKw,
        inverterOverrideKw: inverterSel,
        minSocPercent: minSoc,
        maxSocPercent: maxSoc,
        initialSocPercent: minSoc,
      },
      hourlyPricesByTime: economicsPriceMap.size > 0 ? economicsPriceMap : undefined,
      fixedConsumptionPriceEurPerKwh: fixedConsumption,
      fixedReturnPriceEurPerKwh: fixedReturn,
      feedInFeeEurPerKwh: feedInFee,
      supplierMarkupEurPerKwh: supplierMarkup,
      // Pick on the same savings the cards show.
      adjustRows: rangeWindow
        ? (rows, option) => rows.map((row) => rangedRow(row, { ...rangeWindow, ...option }).row)
        : undefined,
    })
  }, [
    rangeWindow,
    contractScenarios,
    economicsSlots,
    economicsPeriodDays,
    peakInverterKw,
    inverterSel,
    minSoc,
    maxSoc,
    economicsPriceMap,
    fixedConsumption,
    fixedReturn,
    feedInFee,
    supplierMarkup,
  ])

  // Auto-select optimal type + scenario once per dataset (unless the user overrode).
  useEffect(() => {
    scenarioTouchedRef.current = false
    batteryTypeTouchedRef.current = false
    didAutoAdviseRef.current = false
  }, [addressUuid, months])

  // Editing the battery hardware re-opens the advice; editing your own tariffs
  // does not, so typing your rates cannot bounce you onto another scenario.
  useEffect(() => {
    didAutoAdviseRef.current = false
  }, [inverterSel, minSoc, maxSoc])

  useEffect(() => {
    if (status !== 'done' || !autoAdvice) return
    if (didAutoAdviseRef.current) return
    didAutoAdviseRef.current = true
    if (!batteryTypeTouchedRef.current) {
      setBatteryTypeAndPack(autoAdvice.batteryType, false)
    }
    if (!scenarioTouchedRef.current) {
      applyUsageScenario(autoAdvice.scenarioId as UsageScenarioId, false)
    }
  }, [status, autoAdvice])

  const recommended = useMemo(() => (sweep ? recommendCapacity(sweep.rows) : null), [sweep])

  /** All standard sizes for the carousel; only sizes that pay back carry a label. */
  const capacityCards = useMemo(() => {
    if (!sweep || !recommended) return []
    const labels = labelCapacities(sweep.rows, recommended.capacityKwh)
    return STANDARD_BATTERY_CAPACITIES_KWH.map((kwh) => ({
      capacityKwh: kwh,
      variant: labels.get(kwh) ?? null,
      row: sweep.rows.find((r) => r.capacityKwh === kwh) ?? null,
    }))
  }, [sweep, recommended])

  const rangedFor = (row: CapacitySweepRow | null): RangedRow | null =>
    (row && ranged?.find((r) => r.row.capacityKwh === row.capacityKwh)) ?? null
  const rangeFor = (row: CapacitySweepRow | null): SavingsRange | null =>
    rangedFor(row)?.range ?? null
  const showsRanges = capacityCards.some((card) => rangeFor(card.row) != null)
  /** Which way the shown savings moved, when they did for any size. */
  const rangeAdjusted = ranged?.find((r) => r.adjusted)?.adjusted ?? null

  // Keep the selection valid when the sweep changes.
  useEffect(() => {
    if (capacitySel == null) return
    if (!capacityCards.some((o) => o.capacityKwh === capacitySel)) {
      setCapacitySel(null)
    }
  }, [capacityCards, capacitySel])

  const selectedCapacity = capacitySel ?? recommended?.capacityKwh ?? DEFAULT_CAPACITY_KWH
  const selectedRow =
    sweep?.rows.find((r) => r.capacityKwh === selectedCapacity) ?? recommended ?? null
  const selectedRanged = rangedFor(selectedRow)
  const selectedRange = selectedRanged?.range ?? null
  const selectedPaybackRange = selectedRange ? yearsRange(selectedRange) : null

  const selectCapacity = (capacityKwh: number) => {
    const card = capacityCards.find((c) => c.capacityKwh === capacityKwh)
    const row = card?.row
    trackEvent('battery_capacity_select', {
      capacity_kwh: capacityKwh,
      is_recommended: capacityKwh === recommended?.capacityKwh,
      variant: card?.variant ?? 'other',
      payback_years:
        row && Number.isFinite(row.paybackYears) ? Number(row.paybackYears.toFixed(2)) : undefined,
      annual_savings_eur: row ? Math.round(row.annualSavingsEur) : undefined,
      battery_type: batteryType,
    })
    setCapacitySel(capacityKwh)
  }

  const centerCarouselOn = (capacityKwh: number, behavior: ScrollBehavior) => {
    const root = carouselRef.current
    if (!root) return
    const card = root.querySelector<HTMLElement>(`[data-capacity="${capacityKwh}"]`)
    if (!card) return
    const left = card.offsetLeft - (root.clientWidth - card.clientWidth) / 2
    root.scrollTo({ left: Math.max(0, Math.min(left, root.scrollWidth - root.clientWidth)), behavior })
  }

  // Keep the selected card centered in the carousel when possible.
  useEffect(() => {
    if (status !== 'done' || capacityCards.length === 0) return
    const behavior = didCenterCarousel.current ? 'smooth' : 'auto'
    // Wait a frame so layout/widths are settled after cards mount.
    const id = requestAnimationFrame(() => {
      centerCarouselOn(selectedCapacity, behavior)
      didCenterCarousel.current = true
    })
    return () => cancelAnimationFrame(id)
  }, [status, selectedCapacity, capacityCards])

  useEffect(() => {
    didCenterCarousel.current = false
  }, [addressUuid, months, strategy, effectivePriceMode, batteryType])

  const scrollCarousel = (direction: -1 | 1) => {
    const root = carouselRef.current
    if (!root) return
    const card = root.querySelector<HTMLElement>('[data-capacity]')
    const step = card ? card.offsetWidth + 16 : root.clientWidth * 0.8
    root.scrollBy({ left: direction * step, behavior: 'smooth' })
  }

  const selectedInverterKw = selectedRow?.inverterKw ?? peakInverterKw

  const selectedSim = useMemo(() => {
    if (!economicsSlots || !selectedRow) return null
    return runBatterySimulation({
      hourlySlots: economicsSlots,
      tariff,
      capacityKwh: selectedRow.capacityKwh,
      inverterKw: selectedRow.inverterKw,
      minSocPercent: minSoc,
      maxSocPercent: maxSoc,
      initialSocKwh: selectedRow.capacityKwh * (minSoc / 100),
      strategy,
      tradingSharePercent: tradingShare,
    })
  }, [economicsSlots, selectedRow, strategy, tariff, tradingShare, minSoc, maxSoc])

  const roiSeries = useMemo(() => {
    if (!selectedRow || selectedRow.annualSavingsEur <= 0) return []
    const payback = selectedRow.paybackYears
    const horizon = Math.min(
      20,
      Math.max(10, Number.isFinite(payback) ? Math.ceil(payback) + 2 : 12)
    )
    return Array.from({ length: horizon + 1 }, (_, year) => ({
      year,
      saved: Math.round(
        cumulativeSavingsWithDegradation(selectedRow.annualSavingsEur, year)
      ),
    }))
  }, [selectedRow])

  /**
   * Days the selected battery stands still, and why. A trading day with no
   * activity looks like a bug unless the report says the spread was too small
   * to pay for the conversion loss; the bar comes from the planner's own rule.
   */
  const quietDays = useMemo(() => {
    if (!economicsSlots || !selectedSim) return null
    const trades = strategy !== 'self-consumption' && effectivePriceMode === 'spot' && tariff != null
    const spreads = trades ? dailyPriceSpreads(economicsSlots.map((s) => s.time), tariff) : null
    const byDate = new Map<string, { movedWh: number; surplusWh: number }>()
    selectedSim.hourly.forEach((hr, i) => {
      const slot = economicsSlots[i]
      const date = hr.time.slice(0, 10)
      const day = byDate.get(date) ?? { movedWh: 0, surplusWh: 0 }
      day.movedWh += hr.chargeWh + hr.dischargeWh
      if (slot) day.surplusWh += Math.max(0, slot.returned - slot.delivered)
      byDate.set(date, day)
    })

    const reasons = new Map<string, QuietDayReason>()
    for (const [date, day] of byDate) {
      if (day.movedWh >= 100) continue
      const spread = spreads?.get(date)
      if (spread && spread.spreadEurPerKwh < spread.requiredEurPerKwh) {
        reasons.set(date, { kind: 'small-spread', ...spread })
      } else if (strategy !== 'trading' && day.surplusWh < 100) {
        reasons.set(date, { kind: 'no-surplus' })
      }
    }
    const required = spreads ? [...spreads.values()].map((s) => s.requiredEurPerKwh).sort((a, b) => a - b) : []
    const summary: QuietDaysSummary = {
      smallSpreadDays: [...reasons.values()].filter((r) => r.kind === 'small-spread').length,
      noSurplusDays: [...reasons.values()].filter((r) => r.kind === 'no-surplus').length,
      totalDays: byDate.size,
      typicalRequiredEurPerKwh:
        required.length > 0 ? required[Math.floor(required.length / 2)]! : null,
    }
    return { reasons, summary }
  }, [economicsSlots, selectedSim, strategy, effectivePriceMode, tariff])

  const monthly = useMemo(() => {
    if (!economicsSlots || !selectedSim) return []
    const months = monthlySavings(economicsSlots, selectedSim.hourly, strategy, tariff)
    // Moved to the middle of the range: the months follow the year, in proportion.
    const estimate = rawSweep?.rows.find((r) => r.capacityKwh === selectedRanged?.row.capacityKwh)
    if (!selectedRanged?.adjusted || !estimate || estimate.annualGrossEur <= 0) return months
    const factor = selectedRanged.row.annualGrossEur / estimate.annualGrossEur
    return months.map((month) => ({ ...month, savings: month.savings * factor }))
  }, [economicsSlots, selectedSim, strategy, tariff, selectedRanged, rawSweep])

  const buildPdf = async () => {
    if (!data || !sweep || !selectedRow || !selectedSim) return null
    const { buildBatteryPdf } = await import('@/lib/batteryReportPdf')
    const capacityWh = selectedRow.capacityKwh * 1000
    const byMonth = new Map<
      string,
      { chargeWh: number; dischargeWh: number; socSum: number; hours: number }
    >()
    for (const hr of selectedSim.hourly) {
      const month = hr.time.slice(0, 7)
      const slot = byMonth.get(month) ?? { chargeWh: 0, dischargeWh: 0, socSum: 0, hours: 0 }
      slot.chargeWh += hr.chargeWh
      slot.dischargeWh += hr.dischargeWh
      slot.socSum += hr.socEnd / capacityWh
      slot.hours++
      byMonth.set(month, slot)
    }
    const monthlyEnergy = [...byMonth.entries()]
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([month, s]) => ({
        month,
        chargedKwh: s.chargeWh / 1000,
        dischargedKwh: s.dischargeWh / 1000,
        avgSocPct: s.hours > 0 ? (s.socSum / s.hours) * 100 : 0,
      }))

    const byDate = new Map<string, { chargeWh: number; dischargeWh: number; endSocWh: number }>()
    for (const hr of selectedSim.hourly) {
      const date = hr.time.slice(0, 10)
      const slot = byDate.get(date) ?? { chargeWh: 0, dischargeWh: 0, endSocWh: 0 }
      slot.chargeWh += hr.chargeWh
      slot.dischargeWh += hr.dischargeWh
      slot.endSocWh = hr.socEnd
      byDate.set(date, slot)
    }
    const dailyEnergy = [...byDate.entries()]
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([date, s]) => ({
        date,
        chargedKwh: s.chargeWh / 1000,
        dischargedKwh: s.dischargeWh / 1000,
        endSocPct: (s.endSocWh / capacityWh) * 100,
        quietReason: quietDays?.reasons.get(date),
      }))

    return buildBatteryPdf({
      language: 'nl',
      addressLabel,
      fromIso: data.fromIso,
      toIso: data.toIso,
      generatedAt: generatedAt ?? new Date().toISOString(),
      strategy,
      tradingSharePercent: tradingShare,
      priceMode: effectivePriceMode,
      fixedConsumption,
      fixedReturn,
      feedInFeeEurPerKwh: feedInFee,
      supplierMarkupEurPerKwh: supplierMarkup,
      batteryType,
      batteryCostPerKwh,
      capacityKwh: selectedRow.capacityKwh,
      inverterKw: selectedRow.inverterKw,
      recommendedInverterKw: data.inverterKw,
      minSocPercent: minSoc,
      maxSocPercent: maxSoc,
      initialSocPercent: minSoc,
      selection: selectedRow,
      sweepRows: sweep.rows,
      monthlySavings: monthly,
      monthlyEnergy,
      dailyEnergy,
      quietDays: quietDays?.summary,
      totalChargedKwh: selectedSim.totalChargedWh / 1000,
      totalDischargedKwh: selectedSim.totalDischargedWh / 1000,
      usedProfileCompletion: annualSeries?.usedProfileCompletion ?? false,
      solarEstimateUncertain,
      savingsRange: selectedRange,
      savingsAdjusted: selectedRanged?.adjusted ?? null,
      quality: situation
        ? {
            level: dataQuality(situation),
            months: situation.months,
            devices: {
              smartMeter: situation.hasSmartMeter,
              solar: situation.hasSolar,
              battery: situation.hasBattery,
            },
            usedProfileCompletion: annualSeries?.usedProfileCompletion ?? false,
            solarEstimateUncertain,
          }
        : undefined,
    })
  }

  const exportPdf = async () => {
    if (pdfBusy) return
    setPdfBusy(true)
    try {
      const doc = await buildPdf()
      if (doc) {
        doc.save(`easyenergy-batterijadvies-${new Date().toISOString().slice(0, 10)}.pdf`)
        trackEvent('battery_pdf_download', {
          capacity_kwh: selectedRow?.capacityKwh,
          battery_type: batteryType,
          strategy,
          price_mode: effectivePriceMode,
          scenario_id: activeScenarioId ?? undefined,
          payback_years:
            selectedRow && Number.isFinite(selectedRow.paybackYears)
              ? Number(selectedRow.paybackYears.toFixed(2))
              : undefined,
          annual_savings_eur: selectedRow ? Math.round(selectedRow.annualSavingsEur) : undefined,
          was_user_capacity_override: capacitySel != null,
        })
      }
    } finally {
      setPdfBusy(false)
    }
  }

  const errorMessage =
    axios.isAxiosError(error) && typeof error.response?.data?.message === 'string'
      ? error.response.data.message
      : error instanceof Error
        ? error.message
        : 'Er ging iets mis.'

  const availableMonths = situation?.months ?? 12
  const periodOptions = useMemo(() => {
    const opts = [3, 6, 12].filter((m) => m < availableMonths)
    if (availableMonths >= 1) opts.push(availableMonths)
    return [...new Set(opts)].sort((a, b) => a - b)
  }, [availableMonths])

  const startAnalysis = (entry: 'wizard' | 'expert' = 'wizard') => {
    analysisEntryRef.current = entry
    reportReadyKeyRef.current = null
    failTrackedKeyRef.current = null
    setView('report')
    setCapacitySel(null)
    if (!(status === 'done' && data)) run()
  }

  const applyQuickResult = (result: QuickWizardResult) => {
    setContract(result.contract)
    setStrategy(result.strategy)
    setPriceMode(result.priceMode)
    if (!hasFinancialCase(result.contract, result.hasSolar)) {
      setView('no-case')
      return
    }
    startAnalysis('wizard')
  }

  const openExpert = (source: 'wizard' | 'report') => {
    trackEvent('battery_expert_open', { source })
    setView('expert')
  }

  const submitExpert = () => {
    // An expert picked the strategy by hand; auto-advice must not overrule it.
    scenarioTouchedRef.current = true
    trackEvent('battery_expert_submit', {
      strategy,
      price_mode: effectivePriceMode,
      battery_type: batteryType,
      months,
      trading_share: tradingShare,
      has_inverter_override: inverterSel != null,
      min_soc: minSoc,
      max_soc: maxSoc,
    })
    startAnalysis('expert')
  }

  // Report ready / calculation fail — once per analysis run.
  useEffect(() => {
    if (status === 'done' && sweep && selectedRow && data) {
      const key = generatedAt ?? 'done'
      if (reportReadyKeyRef.current === key) return
      reportReadyKeyRef.current = key
      trackEvent('battery_report_ready', {
        entry: analysisEntryRef.current,
        strategy,
        price_mode: effectivePriceMode,
        battery_type: batteryType,
        recommended_capacity_kwh: recommended?.capacityKwh,
        recommended_scenario: activeScenarioId ?? undefined,
        auto_battery_type: autoAdvice?.batteryType,
        payback_years: Number.isFinite(selectedRow.paybackYears)
          ? Number(selectedRow.paybackYears.toFixed(2))
          : undefined,
        annual_savings_eur: Math.round(selectedRow.annualSavingsEur),
        months,
        data_quality: situation ? dataQuality(situation) : undefined,
        used_profile_completion: annualSeries?.usedProfileCompletion ?? false,
        solar_estimate_uncertain: solarEstimateUncertain,
        has_smart_meter: data.hasSmartMeter,
      })
      return
    }
    if (status === 'error' || status === 'cancelled') {
      const key = `${status}:${errorMessage}`
      if (failTrackedKeyRef.current === key) return
      failTrackedKeyRef.current = key
      trackEvent('battery_calculation_fail', {
        outcome: status,
        error_message: status === 'error' ? errorMessage.slice(0, 120) : undefined,
        months,
        entry: analysisEntryRef.current,
      })
    }
  }, [
    status,
    sweep,
    selectedRow,
    data,
    generatedAt,
    strategy,
    effectivePriceMode,
    batteryType,
    recommended,
    activeScenarioId,
    autoAdvice,
    months,
    situation,
    annualSeries,
    errorMessage,
  ])

  const contractAdj = effectivePriceMode === 'spot' ? 'dynamische' : 'vaste'
  const recommendedPaysBackInLife =
    recommended != null &&
    Number.isFinite(recommended.paybackYears) &&
    recommended.paybackYears <= MAX_BATTERY_LIFETIME_YEARS

  /**
   * Payback bars follow the carousel selection: the neighbour smaller, the
   * selected size, and the neighbour larger (clamped at the edges).
   */
  const paybackWindow = useMemo(() => {
    if (capacityCards.length === 0) return []
    const idx = capacityCards.findIndex((c) => c.capacityKwh === selectedCapacity)
    if (idx < 0) return capacityCards.slice(0, 3)
    if (idx === 0) return capacityCards.slice(0, 3)
    if (idx === capacityCards.length - 1) return capacityCards.slice(-3)
    return capacityCards.slice(idx - 1, idx + 2)
  }, [capacityCards, selectedCapacity])

  const maxPaybackForBars = Math.max(
    1,
    ...paybackWindow.map((o) =>
      o.row && Number.isFinite(o.row.paybackYears) ? o.row.paybackYears : 0
    )
  )

  const advisedInverterKw = selectedInverterKw > 0 ? selectedInverterKw : null
  const inverterIsCustom = inverterSel != null
  const paybackBeyondWarranty =
    selectedRow != null &&
    Number.isFinite(selectedRow.paybackYears) &&
    selectedRow.paybackYears > WARRANTY_YEARS

  /** No battery size earns anything: report the outcome instead of empty cards. */
  const noViableBattery =
    status === 'done' && sweep != null && sweep.rows.every((r) => r.annualSavingsEur <= 0)

  const backToQuestions = () => {
    setView('questions')
  }

  const showStepper = view !== 'report' || running || status === 'error' || status === 'cancelled'
  const stepperCurrent: 1 | 2 = view === 'report' || view === 'no-case' ? 2 : 1
  const breakEvenY =
    selectedRow && Number.isFinite(selectedRow.paybackYears)
      ? selectedRow.costEur
      : null
  const breakEvenX =
    selectedRow && Number.isFinite(selectedRow.paybackYears) ? selectedRow.paybackYears : null

  return (
    <div className="space-y-5">
      {showStepper && <AdviceStepper current={stepperCurrent} />}

      {view === 'questions' && (
        <QuickWizard
          situation={situation}
          checkingSolar={situationLoading}
          onComplete={applyQuickResult}
          onOpenExpert={() => openExpert('wizard')}
        />
      )}

      {view === 'no-case' && (
        <NoFinancialCaseCard
          variant="no-mechanism"
          fixedConsumption={fixedConsumption}
          onBack={backToQuestions}
          onOpenExpert={() => openExpert('report')}
        />
      )}

      {view === 'expert' && (
        <>
          <button
            type="button"
            className="text-15 font-semibold text-dark-blue/60 transition-colors hover:text-dark-blue"
            onClick={() => setView('questions')}
          >
            ← Terug naar de vragen
          </button>
          <ExpertForm
            months={months}
            onMonthsChange={(m) => {
              monthsTouchedRef.current = true
              setMonths(m)
            }}
            periodOptions={periodOptions}
            availableMonths={availableMonths}
            strategy={strategy}
            setStrategy={setStrategy}
            tradingShare={tradingShare}
            setTradingShare={setTradingShare}
            feedInFee={feedInFee}
            setFeedInFee={setFeedInFee}
            supplierMarkup={supplierMarkup}
            setSupplierMarkup={setSupplierMarkup}
            batteryType={batteryType}
            setBatteryType={(t) => setBatteryTypeAndPack(t, true)}
            batteryCostPerKwh={batteryCostPerKwh}
            setBatteryCostPerKwh={setBatteryCostPerKwh}
            minSoc={minSoc}
            setMinSoc={setMinSoc}
            maxSoc={maxSoc}
            setMaxSoc={setMaxSoc}
            inverterSel={inverterSel}
            setInverterSel={setInverterSel}
            recommendedInverterKw={data?.inverterKw ?? null}
            submitLabel={status === 'done' && data ? 'Bekijk je rapport' : 'Bereken mijn advies'}
            onSubmit={submitExpert}
          />
        </>
      )}

      {view === 'report' && (
        <>
          {running && (
            <>
              <div className="card space-y-4 p-5 md:p-[30px]">
                <div className="flex items-center gap-3">
                  <Spinner />
                  <div>
                    <p className="text-lg font-semibold text-dark-blue">
                      We maken je persoonlijke rapport
                    </p>
                    <p className="text-base text-dark-blue/60">
                      We halen je energiedata en de stroomprijzen op en rekenen daarna elke
                      batterijgrootte door — dit duurt meestal minder dan een minuut.
                    </p>
                  </div>
                </div>
                <div className="h-2 w-full overflow-hidden rounded-full bg-beige">
                  <div
                    className="h-full rounded-full bg-dark-purple transition-all"
                    style={{ width: `${progressPct}%` }}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <p className="text-13 text-text-gray">
                    Stap {Math.min(progress.done + 1, progress.total)} van {progress.total}
                  </p>
                  <button className="btn-secondary !h-10 !px-4" onClick={cancel}>
                    Annuleren
                  </button>
                </div>
              </div>
              <div className="animate-pulse space-y-5" aria-hidden="true">
                <div className="h-16 rounded-2xl bg-beige" />
                <div className="grid gap-4 md:grid-cols-3">
                  {[0, 1, 2].map((i) => (
                    <div key={i} className="h-72 rounded-3xl bg-beige" />
                  ))}
                </div>
                <div className="h-40 rounded-3xl bg-beige" />
              </div>
            </>
          )}

          {status === 'error' && (
            <div className="card space-y-4 p-5 md:p-[30px]">
              <p className="text-base font-semibold text-red">{errorMessage}</p>
              <div className="flex flex-wrap gap-2">
                <button className="btn-primary" onClick={run}>
                  Probeer opnieuw
                </button>
                <button className="btn-secondary" onClick={() => setView('questions')}>
                  Terug naar de vragen
                </button>
              </div>
            </div>
          )}

          {status === 'cancelled' && !data && (
            <div className="card space-y-4 p-5 md:p-[30px]">
              <p className="text-base text-dark-blue/75">De berekening is gestopt.</p>
              <button className="btn-primary" onClick={run}>
                Start opnieuw
              </button>
            </div>
          )}

          {status === 'done' && data && !data.hasSmartMeter && (
            <div className="card-beige border-l-4 border-orange p-5 text-base leading-normal text-dark-blue">
              <strong>Geen slimme meter gevonden op dit adres.</strong> Voor het batterijadvies
              hebben we slimme-meterdata (P1) nodig. Koppel een Sparky of slimme meter via de
              easyEnergy-app en probeer het daarna opnieuw.
            </div>
          )}
          {status === 'done' &&
            data &&
            data.hasSmartMeter &&
            strategy !== 'trading' &&
            ((situation?.returnedKwh ?? 0) > 0 ||
              data.slots.some((s) => s.returned > 0)) &&
            !(situation?.hasSolar ?? data.hasSolar) &&
            !(situation?.hasBattery ?? false) && (
              <div className="card-beige border-l-4 border-orange p-5 text-base leading-normal text-dark-blue">
                <strong>We zien teruglevering, maar nog geen opwek- of batterijdata.</strong> Op
                je slimme meter komt er stroom terug het net op — dat wijst op zonnepanelen of
                een thuisbatterij. Koppel die in de easyEnergy-app, dan kunnen we je eigen verbruik
                veel scherper berekenen en wordt dit advies beter.
                <div className="mt-3 flex flex-wrap gap-2.5">
                  <a
                    href={APP_STORE_URL}
                    target="_blank"
                    rel="noreferrer"
                    className="btn-secondary !px-4"
                  >
                    Apple App Store
                  </a>
                  <a
                    href={PLAY_STORE_URL}
                    target="_blank"
                    rel="noreferrer"
                    className="btn-secondary !px-4"
                  >
                    Google Play Store
                  </a>
                </div>
              </div>
            )}

          {status === 'done' && data && noViableBattery && (
            <NoFinancialCaseCard
              variant="no-savings"
              fixedConsumption={fixedConsumption}
              onBack={backToQuestions}
              onOpenExpert={() => openExpert('report')}
            />
          )}

          {status === 'done' &&
            sweep &&
            data &&
            selectedRow &&
            !noViableBattery &&
            capacityCards.length > 0 && (
            <>
              {annualSeries?.usedProfileCompletion &&
                (solarEstimateUncertain ? (
                  <div className="card-beige border-l-4 border-orange p-5 text-base leading-normal text-dark-blue">
                    <strong>We hebben je zonnestroom nog niet in beeld.</strong> De periode die we
                    meten bevat bijna geen zonnige maanden, en juist die bepalen hoeveel een
                    batterij voor je kan doen. We vullen de rest van het jaar aan met het landelijke
                    E1A AMI-profiel {NED_PROFILE_YEAR}, maar hoeveel jouw panelen over een heel jaar
                    terugleveren weten we nog niet: dat kan flink hoger of lager uitvallen. Zie de
                    besparing en terugverdientijd hieronder daarom als een richting, niet als een
                    belofte. Meet je door tot en met het voorjaar, dan rekenen we met jouw eigen
                    zonnestroom.
                    {showsRanges && <> {RANGE_EXPLANATION}</>}
                    {rangeAdjusted && <> {RANGE_ADJUSTED[rangeAdjusted]}</>}
                  </div>
                ) : (
                  <div className="card-beige p-5 text-base leading-normal text-dark-blue/70">
                    <strong className="text-dark-blue">Jaarbasis met seizoensprofiel.</strong> We
                    meten nog geen volledig jaar, dus de maanden die ontbreken zijn aangevuld met het
                    landelijke E1A AMI-verbruiksprofiel {NED_PROFILE_YEAR} (afname en teruglevering)
                    in plaats van je meetperiode lineair op te schalen. Wat we wél gemeten hebben,
                    rekenen we mee zoals het is.
                    {showsRanges && <> {RANGE_EXPLANATION}</>}
                    {rangeAdjusted && <> {RANGE_ADJUSTED[rangeAdjusted]}</>}
                  </div>
                ))}
              {/* Page header + PDF download */}
              <div className="flex flex-col gap-5 md:flex-row md:items-start md:justify-between">
                <div className="max-w-2xl">
                  <EasyEnergyLogo variant="color" className="mb-4 h-5 w-auto" />
                  <h2 className="text-28 font-semibold leading-111 lg:text-44">
                    Welke batterij past bij jou?
                  </h2>
                  <p className="mt-3 text-base leading-normal text-dark-blue/70">
                    Doorgerekend op jouw verbruik{hasSolarStrategy ? ', zonopwek' : ''} en{' '}
                    {contractAdj} contract. We selecteren het beste gebruiksscenario en de
                    batterijgrootte met de snelste terugverdientijd die nog zo veel mogelijk eigen
                    verbruik haalt ({WARRANTY_YEARS}–{MAX_BATTERY_LIFETIME_YEARS} jaar
                    levensduur).
                    {recommendedPaysBackInLife
                      ? ' Ons advies verdient zich binnen die periode terug.'
                      : ' Geen van de groottes verdient zich ruim binnen die periode terug; kies dan voorzichtig of wacht op meer data.'}
                  </p>
                </div>
                <button
                  className="btn-purple shrink-0 !h-12 !px-5"
                  onClick={exportPdf}
                  disabled={pdfBusy}
                >
                  {pdfBusy ? <Spinner className="size-4" /> : null}
                  <svg viewBox="0 0 16 16" className="size-4" fill="none" aria-hidden="true">
                    <path
                      d="M8 2v8m0 0L5 7.5M8 10l3-2.5M3 13h10"
                      stroke="currentColor"
                      strokeWidth="1.6"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  Download je batterij rapport
                </button>
              </div>

              {/* Usage scenarios — always on a dynamic (easyEnergy) contract */}
              <div className="space-y-3">
                <div>
                  <p className="field-label">Gebruiksscenario</p>
                  <p className="mt-1 text-15 text-dark-blue/60">
                    We kiezen automatisch het beste batterijtype (plugin of geïnstalleerd) en
                    gebruiksscenario — met de snelste terugverdientijd en zoveel mogelijk eigen
                    verbruik. Alleen handelen wordt pas primair als het duidelijk beter scoort;
                    je kunt type en scenario zelf nog vergelijken.
                  </p>
                </div>
                <div
                  className="grid gap-3 sm:grid-cols-2"
                  role="radiogroup"
                  aria-label="Gebruiksscenario"
                >
                  {USAGE_SCENARIOS.map((scenario) => {
                    const selected = activeScenarioId === scenario.id
                    return (
                      <button
                        key={scenario.id}
                        type="button"
                        role="radio"
                        aria-checked={selected}
                        onClick={() => applyUsageScenario(scenario.id, true)}
                        className={`rounded-2xl border-2 p-4 text-left transition-all duration-300 ${
                          selected
                            ? 'border-dark-purple bg-light-purple-3/50'
                            : 'border-transparent bg-beige hover:bg-beige-2/50'
                        }`}
                      >
                        <span className="block text-base font-semibold text-dark-blue">
                          {scenario.title}
                        </span>
                        <span className="mt-1 block text-15 leading-normal text-dark-blue/60">
                          {scenario.description}
                        </span>
                      </button>
                    )
                  })}
                </div>
              </div>

              {/* Capacity carousel: smaller left → recommended trio → larger right */}
              <div className="relative">
                <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div
                    className="inline-flex w-fit rounded-full bg-beige p-1"
                    role="group"
                    aria-label="Batterijtype"
                  >
                    <button
                      type="button"
                      className={`rounded-full px-4 py-2 text-15 font-semibold transition-colors ${
                        batteryType === 'installed'
                          ? 'bg-dark-purple text-white'
                          : 'text-dark-blue/70 hover:text-dark-blue'
                      }`}
                      onClick={() => setBatteryTypeAndPack('installed', true)}
                    >
                      Geïnstalleerd
                    </button>
                    <button
                      type="button"
                      className={`rounded-full px-4 py-2 text-15 font-semibold transition-colors ${
                        batteryType === 'plugin'
                          ? 'bg-dark-purple text-white'
                          : 'text-dark-blue/70 hover:text-dark-blue'
                      }`}
                      onClick={() => setBatteryTypeAndPack('plugin', true)}
                    >
                      Plugin-batterij
                    </button>
                  </div>
                  <p className="text-15 text-dark-blue/60 sm:text-right">
                    {batteryType === 'plugin'
                      ? 'Plugin: ± €300/kWh, geen installatiebasis, max. 0,5C en 2,5 kW'
                      : 'Geïnstalleerd: ± €400/kWh + €1.000 basis, omvormer 0,5C–1C'}
                  </p>
                </div>
                <div className="mb-3 flex items-center justify-between gap-3">
                  <p className="text-15 text-dark-blue/60">
                    {STANDARD_BATTERY_CAPACITIES_KWH.length} standaardgroottes
                  </p>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      className="circle-btn !bg-beige !text-dark-blue hover:!bg-beige-2"
                      aria-label="Kleinere batterijen"
                      onClick={() => scrollCarousel(-1)}
                    >
                      <svg viewBox="0 0 16 16" className="size-4" fill="none" aria-hidden="true">
                        <path
                          d="M10 3L5 8l5 5"
                          stroke="currentColor"
                          strokeWidth="1.8"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                    <button
                      type="button"
                      className="circle-btn !bg-beige !text-dark-blue hover:!bg-beige-2"
                      aria-label="Grotere batterijen"
                      onClick={() => scrollCarousel(1)}
                    >
                      <svg viewBox="0 0 16 16" className="size-4" fill="none" aria-hidden="true">
                        <path
                          d="M6 3l5 5-5 5"
                          stroke="currentColor"
                          strokeWidth="1.8"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
                <div
                  ref={carouselRef}
                  className="-mx-4 flex snap-x snap-mandatory gap-4 overflow-x-auto px-4 pt-2 pb-3 scroll-smooth md:-mx-0 md:px-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
                >
                  {capacityCards.map(({ variant, capacityKwh, row }) => {
                    const meta = variant ? VARIANT_META[variant] : OTHER_CARD
                    const featured = variant === 'balance'
                    const selected = capacityKwh === selectedCapacity
                    const payback = row?.paybackYears
                    const solarPct = row?.selfConsumptionPercent
                    const range = rangeFor(row)
                    const paybackRange = range ? yearsRange(range) : null
                    return (
                      <div
                        key={capacityKwh}
                        data-capacity={capacityKwh}
                        className={`flex w-[min(100%,300px)] shrink-0 snap-center flex-col rounded-3xl p-5 transition-colors duration-300 md:w-[300px] md:p-6 ${
                          selected
                            ? 'border-2 border-dark-purple bg-light-purple-3/70'
                            : featured
                              ? 'border-2 border-dark-purple/35 bg-light-purple-3/25'
                              : 'border border-beige-2 bg-white'
                        }`}
                      >
                        {meta.tag ? (
                          <span
                            className={`inline-flex w-fit items-center gap-1.5 rounded-full px-3 py-1 text-13 font-semibold ${meta.tagClass}`}
                          >
                            {featured && (
                              <svg
                                viewBox="0 0 12 12"
                                className="size-3"
                                fill="currentColor"
                                aria-hidden="true"
                              >
                                <path d="M6 0.8l1.5 3.05 3.37.49-2.44 2.38.58 3.36L6 8.5l-3.01 1.58.58-3.36L1.13 4.34l3.37-.49L6 .8z" />
                              </svg>
                            )}
                            {meta.tag}
                          </span>
                        ) : (
                          <span className="inline-flex h-7 w-fit items-center rounded-full px-3 text-13 font-semibold text-dark-blue/40">
                            Standaard
                          </span>
                        )}
                        <p
                          className={`mt-4 text-42 font-semibold leading-none ${
                            selected || featured ? 'text-dark-purple' : 'text-dark-blue'
                          }`}
                        >
                          {kwhLabel(capacityKwh)}
                        </p>
                        <p className="mt-2 text-15 text-dark-blue/60">
                          ± {eur(row?.costEur ?? capacityKwh * batteryCostPerKwh)}
                          {batteryType === 'installed' ? ' incl. installatie' : ' (plugin)'}
                        </p>
                        {row != null && row.installBaseEur > 0 ? (
                          <p className="text-13 text-dark-blue/45">
                            {eur(row.packCostEur)} pack + {eur(row.installBaseEur)} installatie
                          </p>
                        ) : null}
                        <dl className="mt-5 space-y-2.5 text-15">
                          <div className="flex justify-between gap-3">
                            <dt className="text-dark-blue/60">Besparing</dt>
                            <dd className="text-right font-semibold text-dark-blue">
                              {eur(row?.annualSavingsEur ?? 0)}/jaar
                              {range && (
                                <span className="block text-13 font-normal text-dark-blue/50">
                                  waarschijnlijk {eurRange(range)}
                                </span>
                              )}
                            </dd>
                          </div>
                          <div className="flex justify-between gap-3">
                            <dt className="text-dark-blue/60">Terugverdientijd</dt>
                            <dd
                              className={`text-right font-semibold ${
                                selected || featured ? 'text-dark-purple' : 'text-dark-blue'
                              }`}
                            >
                              {payback != null ? yearsFmt(payback) : '—'}
                              {paybackRange && (
                                <span className="block text-13 font-normal text-dark-blue/50">
                                  {paybackRange}
                                </span>
                              )}
                            </dd>
                          </div>
                          <div className="flex justify-between gap-3">
                            <dt className="text-dark-blue/60">
                              {hasSolarStrategy ? 'Zonopwek benut' : 'Handelsopbrengst per jaar'}
                            </dt>
                            <dd className="font-semibold text-dark-blue">
                              {hasSolarStrategy
                                ? solarPct != null
                                  ? `${solarPct}%`
                                  : '—'
                                : eur(row?.annualSavingsEur ?? 0)}
                            </dd>
                          </div>
                        </dl>
                        <p className="mt-4 flex-1 text-15 leading-normal text-dark-blue/60">
                          {meta.description(capacityKwh)}
                        </p>
                        <button
                          type="button"
                          className={`mt-6 w-full ${selected || featured ? 'btn-primary' : 'btn-secondary'}`}
                          onClick={() => selectCapacity(capacityKwh)}
                        >
                          {selected ? 'Geselecteerd' : `Kies ${kwhLabel(capacityKwh)}`}
                        </button>
                      </div>
                    )
                  })}
                </div>
              </div>

              {/* Advised inverter — C-rate / type sized for the selected capacity */}
              {advisedInverterKw != null && (
                <div className="card-beige flex flex-col gap-3 p-5 sm:flex-row sm:items-center sm:justify-between md:px-6">
                  <div>
                    <p className="field-label">Geadviseerde omvormer</p>
                    <p className="mt-1 text-26 font-semibold leading-tight text-dark-blue">
                      {advisedInverterKw.toLocaleString('nl-NL', { maximumFractionDigits: 1 })} kW
                    </p>
                    <p className="mt-1 max-w-xl text-15 leading-normal text-dark-blue/60">
                      {inverterIsCustom
                        ? `Zelf ingesteld op ${advisedInverterKw.toLocaleString('nl-NL', {
                            maximumFractionDigits: 1,
                          })} kW voor elke batterijgrootte.`
                        : batteryType === 'plugin'
                          ? `Voor ${kwhLabel(selectedCapacity)}: begrensd tot 0,5C en max. 2,5 kW (plugin), met piekbasis ${peakInverterKw.toLocaleString('nl-NL', { maximumFractionDigits: 1 })} kW.`
                          : `Voor ${kwhLabel(selectedCapacity)}: piekvermogen geklemd tussen 0,5C en 1C (geïnstalleerd), piekbasis ${peakInverterKw.toLocaleString('nl-NL', { maximumFractionDigits: 1 })} kW.`}
                    </p>
                  </div>
                  <button
                    type="button"
                    className="btn-secondary shrink-0 !h-11 !px-4"
                    onClick={() => openExpert('report')}
                  >
                    Pas omvormer aan
                  </button>
                </div>
              )}

              {paybackBeyondWarranty && (
                <div className="card-beige border-l-4 border-orange p-5 text-base leading-normal text-dark-blue">
                  <strong>Terugverdientijd buiten de gebruikelijke garantieperiode.</strong> Bij
                  LFP-thuisbatterijen ligt die vaak rond {WARRANTY_YEARS} jaar
                  {selectedRow.fullEquivalentCyclesPerYear > 0
                    ? ` (circa ${Math.round(selectedRow.fullEquivalentCyclesPerYear)} volle cycli per jaar in deze simulatie)`
                    : ''}
                  . Reken op lagere besparing later door capaciteitsverlies.
                </div>
              )}

              {/* Payback bars: neighbour smaller · selected · neighbour larger */}
              <div className="card p-5 md:p-[30px]">
                <h3 className="text-xl font-semibold lg:text-26">Terugverdientijd vergeleken</h3>
                <p className="mt-1.5 text-base text-dark-blue/60">
                  De gekozen batterij met de maatje kleiner en groter ernaast.
                </p>
                <div className="mt-6 space-y-4">
                  {paybackWindow.map(({ variant, capacityKwh, row }, i) => {
                    const active = capacityKwh === selectedCapacity
                    const barColor = active
                      ? VARIANT_META.balance.barColor
                      : i === 0
                        ? VARIANT_META.compact.barColor
                        : VARIANT_META.roomy.barColor
                    const payback =
                      row && Number.isFinite(row.paybackYears) ? row.paybackYears : null
                    const widthPct =
                      payback != null ? Math.max(8, (payback / maxPaybackForBars) * 100) : 8
                    return (
                      <button
                        type="button"
                        key={capacityKwh}
                        className="block w-full text-left"
                        onClick={() => selectCapacity(capacityKwh)}
                      >
                        <div className="mb-2 flex items-baseline justify-between gap-3">
                          <p
                            className={`text-base font-semibold ${
                              active ? 'text-dark-blue' : 'text-dark-blue/70'
                            }`}
                          >
                            {kwhLabel(capacityKwh)}
                            {active ? (
                              <span className="ml-2 text-13 font-semibold text-dark-purple">
                                Geselecteerd
                              </span>
                            ) : null}
                            {!active && variant === 'balance' ? (
                              <span className="ml-2 text-13 font-semibold text-dark-purple">
                                Ons advies
                              </span>
                            ) : null}
                          </p>
                          <p className="text-base font-semibold text-dark-blue">
                            {payback != null ? yearsFmt(payback) : '—'}
                          </p>
                        </div>
                        <div className="h-3.5 w-full overflow-hidden rounded-full bg-beige">
                          <div
                            className="h-full rounded-full transition-all"
                            style={{ width: `${widthPct}%`, backgroundColor: barColor }}
                          />
                        </div>
                      </button>
                    )
                  })}
                </div>
              </div>

              {/* Payback detail chart — directly under the comparison bars */}
              {roiSeries.length > 0 && selectedRow && (
                <div className="card p-5 md:p-[30px]">
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                    <div>
                      <h3 className="text-xl font-semibold lg:text-26">Meer details</h3>
                      <p className="mt-1 text-base text-dark-blue/60">
                        Wanneer heb je de batterij van {selectedRow.capacityKwh} kWh
                        terugverdiend? Besparing daalt met{' '}
                        {(ANNUAL_DEGRADATION_RATE * 100).toFixed(0)}% per jaar door
                        capaciteitsverlies.
                      </p>
                    </div>
                    <ul className="flex flex-wrap gap-4 text-13 text-dark-blue/70">
                      <li className="flex items-center gap-2">
                        <span className="h-0.5 w-5 bg-dark-purple" aria-hidden="true" />
                        Opgetelde besparing (met degradatie)
                      </li>
                      <li className="flex items-center gap-2">
                        <span
                          className="h-px w-5 border-t border-dashed border-dark-blue"
                          aria-hidden="true"
                        />
                        Investering {eur(selectedRow.costEur)}
                      </li>
                    </ul>
                  </div>
                  <div className="relative mt-4">
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart data={roiSeries} margin={{ top: 16, right: 24, bottom: 8, left: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke={CHART.grid} vertical={false} />
                        <XAxis
                          dataKey="year"
                          type="number"
                          domain={[0, 'dataMax']}
                          tick={{ fontSize: 11, fill: CHART.tick }}
                          tickFormatter={(v) => (v === 0 ? 'nu' : `jaar ${v}`)}
                        />
                        <YAxis
                          tick={{ fontSize: 11, fill: CHART.tick }}
                          width={56}
                          tickFormatter={(v) => `€${Number(v) / 1000}k`}
                        />
                        <Tooltip
                          contentStyle={{
                            borderRadius: 8,
                            border: `1px solid ${CHART.grid}`,
                            fontSize: 12,
                          }}
                          formatter={(value) => [eur(Number(value ?? 0)), 'Opgetelde besparing']}
                          labelFormatter={(v) =>
                            Number(v) === 0 ? 'Nu' : `Na ${v} ${Number(v) === 1 ? 'jaar' : 'jaar'}`
                          }
                        />
                        <ReferenceLine
                          y={selectedRow.costEur}
                          stroke={CHART.investment}
                          strokeDasharray="6 4"
                        />
                        {breakEvenX != null && (
                          <ReferenceLine
                            x={breakEvenX}
                            stroke={CHART.breakEven}
                            strokeDasharray="4 4"
                          />
                        )}
                        <Line
                          type="monotone"
                          dataKey="saved"
                          name="Opgetelde besparing"
                          stroke={CHART.savings}
                          strokeWidth={2.5}
                          dot={false}
                          isAnimationActive={false}
                        />
                        {breakEvenX != null && breakEvenY != null && (
                          <ReferenceDot
                            x={breakEvenX}
                            y={breakEvenY}
                            r={6}
                            fill={CHART.breakEven}
                            stroke="#fff"
                            strokeWidth={2}
                          />
                        )}
                      </LineChart>
                    </ResponsiveContainer>
                    {breakEvenX != null && (
                      <div className="pointer-events-none absolute right-4 top-10 max-w-[220px] rounded-2xl bg-dark-blue px-4 py-3 text-white shadow-btn-shadow sm:right-8">
                        <p className="text-15 font-semibold">
                          Terugverdiend na {yearsFmt(breakEvenX).replace(' jaar', '')} jaar
                        </p>
                        <p className="mt-0.5 text-13 text-white/75">
                          start ± {eur(selectedRow.annualSavingsEur)}/jaar, daarna dalend door
                          degradatie
                        </p>
                        {selectedPaybackRange && (
                          <p className="mt-0.5 text-13 text-white/75">
                            bandbreedte: {selectedPaybackRange}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Short wrap-up — method details stay behind a toggle */}
              <section className="space-y-4">
                <div className="card p-5 md:p-[30px]">
                  <h3 className="text-xl font-semibold lg:text-26">Over dit advies</h3>
                  <p className="mt-3 max-w-3xl text-base leading-normal text-dark-blue/70">
                    We hebben {months}{' '}
                    {months === 1 ? 'maand' : 'maanden'} van jouw slimme-meterdata doorgerekend
                    {hasSolarStrategy ? ' (verbruik en teruglevering)' : ''} met{' '}
                    {effectivePriceMode === 'spot' ? 'dynamische uurprijzen' : 'vaste tarieven'}.
                    Daaruit kozen we het scenario, batterijtype en de grootte met de beste
                    terugverdientijd én zo veel mogelijk eigen verbruik
                    {selectedRow
                      ? ` — nu ${kwhLabel(selectedRow.capacityKwh)}, ${
                          batteryType === 'plugin' ? 'plugin' : 'geïnstalleerd'
                        }, terugverdiend in ${yearsFmt(selectedRow.paybackYears)}${
                          selectedPaybackRange ? ` (waarschijnlijk ${selectedPaybackRange})` : ''
                        }`
                      : ''}
                    .
                  </p>
                  <div className="mt-5 flex flex-wrap items-center gap-3">
                    <button
                      type="button"
                      className="btn-secondary !h-11 !px-5"
                      aria-expanded={showMethodDetails}
                      onClick={() => setShowMethodDetails((v) => !v)}
                    >
                      {showMethodDetails ? 'Verberg rekenmethode' : 'Bekijk hoe we rekenen'}
                    </button>
                    <button
                      type="button"
                      className="btn-secondary !h-11 !px-5"
                      onClick={() => openExpert('report')}
                    >
                      Pas de instellingen aan
                    </button>
                  </div>
                </div>

                {showMethodDetails && (
                  <div className="card space-y-8 p-5 md:p-[30px]">
                    <div>
                      <h4 className="text-lg font-semibold text-dark-blue">
                        Hoe we tot dit advies komen
                      </h4>
                      <ol className="mt-3 list-decimal space-y-2 pl-5 text-base leading-normal text-dark-blue/70">
                        <li>
                          We gebruiken jouw echte meetdata van de slimme meter
                          {annualSeries?.usedProfileCompletion
                            ? ` en vullen ontbrekende seizoenen aan met het landelijke E1A AMI-profiel ${NED_PROFILE_YEAR}, zodat zomer én winter meetellen`
                            : ', inclusief zomer en winter voor zover beschikbaar'}
                          .
                        </li>
                        <li>
                          Per batterijgrootte simuleren we uur voor uur laden en ontladen: wanneer
                          er overschot is gaat energie de batterij in, bij tekort komt die weer
                          beschikbaar voor je huis (of voor handelen op uurprijzen).
                        </li>
                        <li>
                          We vergelijken scenario’s (zelfverbruik, handelen, of een mix) en types
                          (plugin of geïnstalleerd). Alleen handelen wint alleen als het duidelijk
                          beter scoort; anders geven we voorrang aan eigen verbruik.
                        </li>
                        <li>
                          De aanbevolen grootte combineert een snelle terugverdientijd met zo hoog
                          mogelijk eigen verbruik, binnen de gebruikelijke levensduur van{' '}
                          {WARRANTY_YEARS}–{MAX_BATTERY_LIFETIME_YEARS} jaar.
                        </li>
                      </ol>
                    </div>

                    {quietDays &&
                      (quietDays.summary.smallSpreadDays > 0 ||
                        quietDays.summary.noSurplusDays > 0) && (
                        <div>
                          <h4 className="text-lg font-semibold text-dark-blue">
                            Waarom staat de batterij sommige dagen stil?
                          </h4>
                          <div className="mt-3 space-y-3 text-base leading-normal text-dark-blue/70">
                            {quietDays.summary.typicalRequiredEurPerKwh != null &&
                              quietDays.summary.smallSpreadDays > 0 && (
                                <p>
                                  Handelen loont alleen als het duurste uur van de dag genoeg boven
                                  het goedkoopste uitkomt. Bovenop de uurprijs betaal je
                                  energiebelasting, opslag en btw, en bij elk laad-ontlaadrondje gaat{' '}
                                  {Math.round((1 - ROUND_TRIP_EFFICIENCY) * 100)}% verloren — stroom
                                  die je tegen de volle prijs hebt ingekocht. Daardoor was in deze
                                  periode meestal een verschil van ongeveer{' '}
                                  <strong className="text-dark-blue">
                                    {Math.round(quietDays.summary.typicalRequiredEurPerKwh * 100)} ct
                                    per kWh
                                  </strong>{' '}
                                  nodig. Op {quietDays.summary.smallSpreadDays} van de{' '}
                                  {quietDays.summary.totalDays} dagen was het verschil kleiner,
                                  vooral op rustige winter- en weekenddagen. Dan laat de batterij de
                                  handel die dag bewust liggen: een rondje zou meer kosten dan het
                                  oplevert.
                                </p>
                              )}
                            {quietDays.summary.noSurplusDays > 0 && (
                              <p>
                                Zonnestroom bewaren kan alleen op dagen met een overschot. Op{' '}
                                {quietDays.summary.noSurplusDays} van de{' '}
                                {quietDays.summary.totalDays} dagen leverden de panelen niets terug
                                aan het net, dus viel er niets op te slaan.
                              </p>
                            )}
                            <p>
                              In de PDF zie je per dag wat de batterij doet, met de reden als hij
                              stilstaat.
                            </p>
                          </div>
                        </div>
                      )}

                    <div>
                      <h4 className="text-lg font-semibold text-dark-blue">
                        Wat we meenemen in de berekening
                      </h4>
                      <dl className="mt-3 grid gap-3 sm:grid-cols-2">
                        {[
                          {
                            label: 'Jouw data',
                            value: `${months} ${months === 1 ? 'maand' : 'maanden'} slimme-meterdata`,
                          },
                          {
                            label: 'Stroomprijzen',
                            value:
                              effectivePriceMode === 'spot'
                                ? 'Dynamische uurprijzen (EPEX)'
                                : `Vast: ${fixedConsumption.toFixed(2).replace('.', ',')} €/kWh · teruglevering ${fixedReturn.toFixed(2).replace('.', ',')} €/kWh`,
                          },
                          {
                            label: 'Batterijtype',
                            value:
                              batteryType === 'plugin'
                                ? `Plugin (± €${batteryCostPerKwh}/kWh, geen installatiebasis)`
                                : `Geïnstalleerd (± €${batteryCostPerKwh}/kWh + €1.000 basis)`,
                          },
                          {
                            label: 'Omvormer',
                            value: `${(selectedRow?.inverterKw ?? peakInverterKw).toLocaleString('nl-NL', { maximumFractionDigits: 1 })} kW (afgestemd op pieken en C-tarief)`,
                          },
                          {
                            label: 'Rondritrendement',
                            value: `${(ROUND_TRIP_EFFICIENCY * 100).toFixed(0)}% — bij elk laad/ontlaad-rondje gaat iets verloren`,
                          },
                          {
                            label: 'Stand-by',
                            value: `${STANDBY_KWH_PER_YEAR} kWh/jaar verbruik van de batterij zelf, afgetrokken van de besparing`,
                          },
                          {
                            label: 'Capaciteitsverlies',
                            value: `${(ANNUAL_DEGRADATION_RATE * 100).toFixed(0)}% per jaar in de terugverdientijdgrafiek`,
                          },
                          {
                            label: 'Handelsmarge',
                            value: `×${TRADING_REALISM_FACTOR.toFixed(2).replace('.', ',')} op pure handelswinst (onzekerheid in de markt)`,
                          },
                          {
                            label: 'Levensduur',
                            value: `We optimaliseren over ±${EXPECTED_BATTERY_LIFETIME_YEARS} jaar (garantie vaak ±${WARRANTY_YEARS} jaar)`,
                          },
                          {
                            label: 'Strategie',
                            value:
                              USAGE_SCENARIOS.find((s) => s.id === activeScenarioId)?.title ??
                              'Huidige instelling',
                          },
                        ].map((item) => (
                          <div key={item.label} className="rounded-2xl bg-beige p-4">
                            <dt className="text-13 font-semibold uppercase tracking-wide text-dark-blue/50">
                              {item.label}
                            </dt>
                            <dd className="mt-1 text-15 leading-normal text-dark-blue">
                              {item.value}
                            </dd>
                          </div>
                        ))}
                      </dl>
                    </div>
                  </div>
                )}

                <div className="card flex flex-col gap-5 overflow-hidden p-5 sm:flex-row sm:items-center md:p-6">
                  <img
                    src="/Sparky-Perspective.png"
                    alt="Sparky en de easyEnergy-app"
                    className="mx-auto h-36 w-auto object-contain sm:mx-0 sm:h-40"
                  />
                  <div className="min-w-0 flex-1">
                    <h4 className="text-lg font-semibold text-dark-blue">
                      Scherper advies met meer apparaten
                    </h4>
                    <p className="mt-1.5 text-base leading-normal text-dark-blue/60">
                      Dit advies komt van jouw Sparky. Koppel ook je zonnepanelen-omvormer of
                      thuisbatterij in de easyEnergy-app voor een preciezere berekening.
                    </p>
                    <div className="mt-4 flex flex-wrap items-center gap-3">
                      <a
                        href={APP_STORE_URL}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex h-11 items-center gap-2 rounded-20 border border-beige-2 bg-white px-4 text-15 font-semibold text-dark-blue transition-all duration-300 hover:bg-beige"
                      >
                        <img src="/apple-app-store.svg" alt="" className="h-5 w-auto" />
                        App Store
                      </a>
                      <a
                        href={PLAY_STORE_URL}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex h-11 items-center gap-2 rounded-20 border border-beige-2 bg-white px-4 text-15 font-semibold text-dark-blue transition-all duration-300 hover:bg-beige"
                      >
                        <img src="/google-play-store.svg" alt="" className="h-5 w-auto" />
                        Google Play
                      </a>
                    </div>
                  </div>
                </div>
              </section>
            </>
          )}

          {status === 'done' && data && data.slots.length === 0 && data.hasSmartMeter && (
            <div className="card p-5 text-center text-base text-dark-blue/75">
              Geen energiedata gevonden voor deze periode.
            </div>
          )}
        </>
      )}
    </div>
  )
}
